import { Listing, CoreListingUpdateState } from '@reverbdotcom/commons/src/rql';
import React, { useState } from 'react';
import { camelize } from 'humps';
import I18n from 'i18n-js';
import ListingRowBumpButtonV2 from './footer_buttons/listing_row_bump_button_v2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import AutoOfferModalButtonV2 from './footer_buttons/auto_offer_modal_button_v2';
import ListingRowEditButton from './footer_buttons/listing_row_edit_button';
import ListingRowRelistButton from './footer_buttons/listing_row_relist_button';
import ListingRowEndButton from './footer_buttons/listing_row_end_button';
import ListingRowPublishButton from './footer_buttons/listing_row_publish_button';
import ListingRowDeleteButton from './footer_buttons/listing_row_delete_button';
import SalesModalButton from './footer_buttons/sales/SalesModalButton';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import updateListingStateMutation from './footer_buttons/update_listing_state_mutation';

const LIVE = 'live';
const SUSPENDED = 'suspended';
const SOLD = 'ordered';
const DRAFT = 'draft';

export const SELLER_SITE = 'seller_site';
interface IExternalProps {
  listing: Listing;
}
interface IListingStateProps {
  listing: Listing;
}

interface IDraftDeleteErrorsProps {
  draftDeleteErrors?: Array<string>;
  setDraftDeleteErrors: React.Dispatch<React.SetStateAction<Array<string>>>;
}

export function ListingState({ listing }: IListingStateProps) {
  const isLive = listing.state === LIVE;
  const isSuspended = listing.state === SUSPENDED;

  let colorToken = 'text-color-gray-700';

  if (isLive) {
    colorToken = 'text-color-green-600';
  } else if (isSuspended) {
    colorToken = 'text-color-red-500';
  }

  return (
    <div className={`weight-bold size-90 mb-2 ${colorToken} tablet-fx-order-1`}>
      {I18n.t(
        `discovery.listingsManagement.sellerListingCard.states.${camelize(
          listing.state,
        )}`,
      ).toUpperCase()}
    </div>
  );
}

export function FooterButtons({ listing }: IExternalProps) {
  const isListingLive = listing.state === LIVE;
  const isSellerSiteExclusive = listing.exclusiveChannel == SELLER_SITE;

  const bumpRate = listing.bumpRate?.rate || 0;
  const isBumpEligible = isListingLive && !isSellerSiteExclusive;
  const isAutoOffersEnabled = listing.autoOffers.id !== '';
  const showSaleButton = isListingLive && !isSellerSiteExclusive && listing.shop.canCurateSales;

  return (
    <div className="d-flex fx-justify-center gap-3 tablet-fx-dir-col tablet-fx-align-center width-100">
      {showSaleButton && (
        <div className="tablet-width-100">
          <SalesModalButton
            listingId={listing.id}
          />
        </div>
      )}
      {isBumpEligible && (
        <div className="tablet-width-100">
          <ListingRowBumpButtonV2
            listingId={listing.id}
            bid={bumpRatePercentToDecimal(bumpRate)}
            refetch={() => {}}
          />
        </div>
      )}
      {listing.isAutoOfferEligible && (
        <div className="tablet-width-100">
          <AutoOfferModalButtonV2
            listingId={listing.id}
            offerActive={isAutoOffersEnabled}
          />
        </div>
      )}
    </div>
  );
}

export function FooterActionButtons({ listing, setDraftDeleteErrors }: IExternalProps & IDraftDeleteErrorsProps) {
  const isEditable = listing.state !== SOLD || listing.relistable;

  const [updateListingState] =
  useMutation< CoreListingUpdateState.Mutation, CoreListingUpdateState.Variables>(
    updateListingStateMutation,
  );

  if (listing.state === DRAFT) {
    return (
      <div className="d-flex gap-3 tablet-width-100 tablet-fx-align-center">
        <ListingRowDeleteButton setDraftDeleteErrors={setDraftDeleteErrors} />
        <ListingRowEditButton listing={listing} />
        <div className="fx-grow">
          <ListingRowPublishButton updateListingState={updateListingState}/>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex gap-3 tablet-fx-dir-col tablet-fx-align-center tablet-width-100 tablet-fx-dir-col-reverse">
      {listing.relistable && (
        <div className="tablet-width-100">
          <ListingRowRelistButton listing={listing} />
        </div>
      )}
      {listing.state == LIVE && (
        <div className="tablet-width-100">
          <ListingRowEndButton updateListingState={updateListingState}/>
        </div>
      )}
      {isEditable && (
        <div className="tablet-width-100">
          <ListingRowEditButton listing={listing} />
        </div>
      )}
    </div>
  );
}

export function DeleteDraftListingErrors({
  draftDeleteErrors = [],
  setDraftDeleteErrors,
}: IDraftDeleteErrorsProps) {
  return (
    <div className="pb-8 pl-6 pr-4">
      <RCAlertBox
        type="error"
        onDismiss={() => {
          setDraftDeleteErrors([]);
        }}
      >
        {I18n.t(
          'discovery.listingsManagement.sellerListingCard.deleteDraftError',
        )}
        <ul className="ml-4">
          { draftDeleteErrors.map((errorMessage) => (
            <li key={errorMessage}>{errorMessage}</li>
          )) }
        </ul>
      </RCAlertBox>
    </div>
  );
}

export default function SellerListingCardFooter({ listing }: IExternalProps) {
  const [draftDeleteErrors, setDraftDeleteErrors] = useState([]);
  return (
    <div className="seller-listing-card-footer">
      <div className="d-flex fx-justify-between fx-align-center tablet-fx-dir-col-reverse tablet-fx-align-start tablet-gap-3 pt-0 pl-4 pr-4 pb-4">
        <ListingState listing={listing} />
        <FooterButtons listing={listing} />
        <FooterActionButtons
          listing={listing}
          setDraftDeleteErrors={setDraftDeleteErrors}
        />
      </div>
      {draftDeleteErrors.length > 0 && (
        <DeleteDraftListingErrors
          draftDeleteErrors={draftDeleteErrors}
          setDraftDeleteErrors={setDraftDeleteErrors}
        />
      )}
    </div>
  );
}
